/**
 * Turns a `LibrarySound` into the buffer that gets written to the blob store.
 *
 * The stages are the ones `LibraryRenderer.swift` runs, in the same order and with the same
 * constants: mix the layers at their own gains and offsets, run the post chain, remove
 * anything below hearing, trim the inaudible tail, match the family's loudness, round off
 * whatever that pushed over the ceiling, and fade the very ends so nothing steps.
 */

import { clamp, effectChainActiveNodes, type EffectChain } from '../../model/types';
import {
  Biquad,
  applyEffectChain,
  chainTailSeconds,
  dbToLinear,
  frameCount,
  linearToDB,
  panGains,
  peakOf,
  scaleChannels,
  type RenderBudget,
  type RenderedAudio,
} from '../offline-render';
import { renderPatch } from '../synth';
import { activeLevelDB } from './loudness';
import { libraryCategoryLevelPolicy, type LibraryLayer, type LibrarySound } from './model';

/**
 * Ceiling every finished sound sits under. −1 dBFS leaves room for the codec's intersample
 * peaks and for a pad being stacked with three others before the master limiter ever has to
 * work.
 */
export const LIBRARY_CEILING_DB = -1.0;

/**
 * Widest correction the level stage is allowed to make. A recipe that needs more than this is
 * mis-designed, and quietly applying +14 dB would hide that behind a wall of soft clipping.
 */
const MAXIMUM_BOOST_DB = 6.0;
const MAXIMUM_CUT_DB = -14.0;

/**
 * Everything under this, relative to the finished peak, is inaudible tail. Trimming it keeps
 * the files tight and stops a 4-second reverb allowance from turning a snare into a 4-second
 * file that is silent for three of them.
 */
const SILENCE_FLOOR_DB = -72.0;

/** Kept after the last audible sample so a decay is never clipped mid-slope. */
const TAIL_PADDING_SECONDS = 0.008;

const FADE_OUT_SECONDS = 0.004;
const FADE_IN_SECONDS = 0.0006;

/**
 * Sub-audible rumble and any DC the oscillators' first half-cycle leaves behind are removed
 * here. At 15 Hz a 32 Hz 808 loses less than a fifth of a dB.
 */
const DC_BLOCK_HZ = 15.0;

export interface RenderLibraryOptions {
  sampleRate?: number;
  budget?: RenderBudget;
  /**
   * `false` skips the level stage and leaves every layer mix at its raw balance. Only the
   * measurement tools use it, to see what a recipe naturally produces before the targets are
   * applied to it.
   */
  levelMatched?: boolean;
}

/** Renders a sound to a levelled, click-free stereo buffer. */
export async function renderLibrarySound(
  sound: LibrarySound,
  options: RenderLibraryOptions = {},
): Promise<RenderedAudio> {
  const sampleRate = clamp(options.sampleRate ?? 48000, 8000, 192000);
  const budget = options.budget;

  let channels = await mixLayers(sound.layers, sound.post, sampleRate, budget);
  if (effectChainActiveNodes(sound.post).length > 0) {
    channels = await applyEffectChain(channels, sound.post, {
      sampleRate,
      tempo: 120,
      ...(budget !== undefined ? { budget } : {}),
    });
  }

  removeSubsonics(channels, sampleRate);
  channels = trimTail(channels, sampleRate);

  if (options.levelMatched !== false) {
    applyLevel(channels, sound, sampleRate);
  }
  applyCeiling(channels);
  applyEdgeFades(channels, sampleRate);
  return { channels, sampleRate };
}

// MARK: - Mixing

async function mixLayers(
  layers: readonly LibraryLayer[],
  chain: EffectChain,
  sampleRate: number,
  budget: RenderBudget | undefined,
): Promise<Float32Array[]> {
  const rendered: { channels: Float32Array[]; offset: number; layer: LibraryLayer }[] = [];
  let frames = 0;

  for (const layer of layers) {
    const audio = await renderPatch(layer.patch, {
      sampleRate,
      ...(budget !== undefined ? { budget } : {}),
    });
    const length = frameCount(audio.channels);
    if (length === 0) continue;
    const offset = Math.max(0, Math.round(layer.delay * sampleRate));
    frames = Math.max(frames, offset + length);
    rendered.push({ channels: audio.channels, offset, layer });
  }

  // Room for whatever the chain adds after the last layer stops; the tail trim gives back
  // anything the effects did not actually use.
  frames += Math.trunc(chainTailSeconds(chain) * sampleRate);
  if (frames <= 0) {
    const silence = Math.max(1, Math.trunc(0.02 * sampleRate));
    return [new Float32Array(silence), new Float32Array(silence)];
  }

  const mix = [new Float32Array(frames), new Float32Array(frames)];
  for (const entry of rendered) {
    mixInto(mix, entry.channels, entry.offset, dbToLinear(entry.layer.gainDB), entry.layer.pan);
  }
  return mix;
}

/**
 * Adds `source` into `destination` at `atFrame`, applying `gain` and `pan`. A negative
 * `atFrame` drops the part of the source that falls before the buffer.
 *
 * Pan follows the live mixer so an offline bounce sounds like the board: a mono source is
 * panned constant-power, while a source that already has a stereo image is *balanced* —
 * centre leaves it untouched and moving the control attenuates the far side.
 */
export function mixInto(
  destination: Float32Array[],
  source: readonly Float32Array[],
  atFrame: number,
  gain: number,
  pan: number,
): void {
  if (destination.length === 0 || source.length === 0 || gain === 0 || !Number.isFinite(gain)) return;
  const destinationFrames = frameCount(destination);
  const sourceFrames = frameCount(source);
  if (destinationFrames === 0 || sourceFrames === 0) return;

  const sourceOffset = Math.max(0, -atFrame);
  const destinationOffset = Math.max(0, atFrame);
  if (destinationOffset >= destinationFrames || sourceOffset >= sourceFrames) return;
  const count = Math.min(sourceFrames - sourceOffset, destinationFrames - destinationOffset);
  if (count <= 0) return;

  const raw = panGains(pan);
  const isMonoSource = source.length === 1;
  const normalisation = isMonoSource ? 1 : 1 / Math.max(raw.left, raw.right);
  const scalars = [gain * raw.left * normalisation, gain * raw.right * normalisation];

  for (let channel = 0; channel < destination.length; channel++) {
    const input = source[Math.min(channel, source.length - 1)]!;
    const output = destination[channel]!;
    const scalar = scalars[channel] ?? gain;
    for (let index = 0; index < count; index++) {
      output[destinationOffset + index]! += input[sourceOffset + index]! * scalar;
    }
  }
}

// MARK: - Finishing

function removeSubsonics(channels: Float32Array[], sampleRate: number): void {
  for (const channel of channels) {
    const filter = new Biquad().set('highPass', DC_BLOCK_HZ, 0.707, sampleRate);
    for (let index = 0; index < channel.length; index++) channel[index] = filter.process(channel[index]!);
  }
}

function trimTail(channels: Float32Array[], sampleRate: number): Float32Array[] {
  const frames = frameCount(channels);
  if (frames === 0) return channels;
  const peak = peakOf(channels);
  if (peak <= 1e-6) return channels;

  const floor = peak * dbToLinear(SILENCE_FLOOR_DB);
  let last = -1;
  for (let index = frames - 1; index >= 0; index--) {
    let loudest = 0;
    for (const channel of channels) loudest = Math.max(loudest, Math.abs(channel[index]!));
    if (loudest > floor) {
      last = index;
      break;
    }
  }
  if (last < 0) return channels;

  const keep = Math.min(
    frames,
    Math.max(Math.trunc(0.02 * sampleRate), last + 1 + Math.trunc(TAIL_PADDING_SECONDS * sampleRate)),
  );
  if (keep >= frames) return channels;
  return channels.map((channel) => channel.slice(0, keep));
}

function applyLevel(channels: Float32Array[], sound: LibrarySound, sampleRate: number): void {
  const policy = libraryCategoryLevelPolicy(sound.category);
  let correction: number;
  if (policy.kind === 'loudness') {
    const measured = activeLevelDB(channels, sampleRate);
    if (!Number.isFinite(measured)) return;
    correction = policy.targetDB - measured;
  } else {
    const peak = peakOf(channels);
    if (peak <= 1e-6) return;
    correction = policy.targetDB - linearToDB(peak);
  }
  scaleChannels(channels, dbToLinear(clamp(correction, MAXIMUM_CUT_DB, MAXIMUM_BOOST_DB)));
}

/**
 * Soft knee from 60 % of the ceiling upward.
 *
 * A drum that has just been pushed up to match the kit's loudness usually has a few dozen
 * samples of transient sitting over the ceiling. Scaling the whole buffer down to fit them
 * would undo the loudness match; rounding those samples off does not, and rounding a drum
 * transient is what a mix engineer does to it anyway. `tanh` is asymptotic, so the output
 * cannot reach the ceiling however hard it is hit.
 */
function applyCeiling(channels: Float32Array[]): void {
  const ceiling = dbToLinear(LIBRARY_CEILING_DB);
  if (peakOf(channels) <= ceiling) return;

  const knee = ceiling * 0.6;
  const span = ceiling - knee;
  for (const channel of channels) {
    for (let index = 0; index < channel.length; index++) {
      const value = channel[index]!;
      const magnitude = Math.abs(value);
      if (magnitude <= knee) continue;
      const shaped = knee + span * Math.tanh((magnitude - knee) / span);
      channel[index] = value < 0 ? -shaped : shaped;
    }
  }
}

function applyEdgeFades(channels: Float32Array[], sampleRate: number): void {
  const frames = frameCount(channels);
  if (frames <= 2) return;

  // Only faded when the buffer actually opens on a step. Every patch attacks from zero, so
  // this normally does nothing — and a blanket fade-in, however short, would round off the
  // transient that makes a drum a drum.
  let opening = 0;
  for (const channel of channels) opening = Math.max(opening, Math.abs(channel[0]!));
  const fadeIn = Math.min(Math.trunc(frames / 4), Math.max(2, Math.trunc(FADE_IN_SECONDS * sampleRate)));
  // A single-sample fade would divide by zero below. Only reachable on a buffer of a handful
  // of samples, which the tail trim's 20 ms floor already rules out — but a NaN in an audio
  // buffer is silent until it reaches a speaker, so it is guarded rather than reasoned about.
  if (opening > 0.002 && fadeIn > 1) {
    const fade = fadeIn;
    for (const channel of channels) {
      for (let offset = 0; offset < fade; offset++) {
        channel[offset]! *= 0.5 * (1 - Math.cos(Math.PI * (offset / (fade - 1))));
      }
    }
  }

  const fade = Math.min(Math.trunc(frames / 2), Math.max(2, Math.trunc(FADE_OUT_SECONDS * sampleRate)));
  const start = frames - fade;
  for (const channel of channels) {
    for (let offset = 0; offset < fade; offset++) {
      // Raised cosine: arrives at zero with zero slope, so nothing steps at the end.
      channel[start + offset]! *= 0.5 * (1 + Math.cos(Math.PI * (offset / (fade - 1))));
    }
  }
}
